import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Lock } from 'lucide-react';
import { useDashboardStore } from '../store/dashboardStore';
import { Staff } from '../types';
import { playInputFeedback } from '../lib/feedback';
import { PIN_SCREEN_BG, useBodyBackgroundOnMount } from '../hooks/useBodyBackgroundOnMount';

interface PinLoginProps {
  // PIN is NOT authentication. This is used only for operator switch / unlock on a device session.
  onSuccess: (staff: Staff) => void;
}

export default function PinLogin({ onSuccess }: PinLoginProps) {
  const { allStaff, initialize } = useDashboardStore();
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);
  const [staffLoading, setStaffLoading] = useState(true);

  useEffect(() => {
    // スタッフリストを初期化
    initialize().finally(() => setStaffLoading(false));
  }, [initialize]);

  useBodyBackgroundOnMount(PIN_SCREEN_BG);

  // PIN入力時にエラーをクリア
  const clearError = useCallback(() => {
    if (pinError) setPinError(null);
  }, [pinError]);

  // エラーメッセージを2秒後に自動消去
  useEffect(() => {
    if (!pinError) return;
    const timer = setTimeout(() => setPinError(null), 2000);
    return () => clearTimeout(timer);
  }, [pinError]);

  const handleLogin = async () => {
  
    if (pin.length !== 4) {
      console.log('PIN長さエラー:', pin.length);
      setPinError('4桁のPINを入力してください');
      return;
    }

    setLoading(true);
    try {
      const staff = allStaff.find(s => String(s.pin) === String(pin) && s.isActive);
      if (!staff) throw new Error('Invalid PIN');
      onSuccess(staff);
    } catch (error: any) {
      console.error('Login error:', error);
      setPinError('PINが正しくありません');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const handleNumberClick = (number: string) => {
    if (pin.length < 4) {
      playInputFeedback();
      clearError();
      const newPin = pin + number;
  
      setPin(newPin);
      
      // 4桁になったら自動でログイン
      if (newPin.length === 4) {
  
        setTimeout(() => handleLoginWithPin(newPin), 100);
      }
    }
  };

  const handleLoginWithPin = async (pinToUse: string) => {

    if (pinToUse.length !== 4) {
      console.log('PIN長さエラー:', pinToUse.length);
      setPinError('4桁のPINを入力してください');
      return;
    }

    setLoading(true);
    try {
      const staff = allStaff.find(s => String(s.pin) === String(pinToUse) && s.isActive);
      if (!staff) throw new Error('Invalid PIN');
      onSuccess(staff);
    } catch (error: any) {
      console.error('Login error:', error);
      setPinError('PINが正しくありません');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    playInputFeedback();
    setPin('');
    clearError();
  };

  const handleBackspace = () => {
    playInputFeedback();
    setPin(prev => prev.slice(0, -1));
    clearError();
  };



  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key >= '0' && e.key <= '9' && pin.length < 4) {
      clearError();
      const newPin = pin + e.key;
      setPin(newPin);
      
      // 4桁になったら自動でログイン
      if (newPin.length === 4) {
        setTimeout(() => handleLoginWithPin(newPin), 100);
      }
    } else if (e.key === 'Backspace') {
      handleBackspace();
    } else if (e.key === 'Enter') {
      handleLogin();
    }
  };

  // 開発用: タイトルを3回タップで再読み込み（PWAでコンテキストメニューが出ない場合に使う）
  const reloadTapCount = useRef(0);
  const reloadTapTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleReloadTap = useCallback(() => {
    if (reloadTapTimer.current) clearTimeout(reloadTapTimer.current);
    reloadTapCount.current += 1;
    if (reloadTapCount.current >= 3) {
      reloadTapCount.current = 0;
      window.location.reload();
    } else {
      reloadTapTimer.current = setTimeout(() => { reloadTapCount.current = 0; }, 1000);
    }
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col overflow-y-auto px-4"
      style={{
        backgroundColor: PIN_SCREEN_BG,
        // iOS: ログイン直後は 100dvh がキーボード退場中に短くなり body が透けることがある。fixed inset-0 で可視領域に張り付ける
        paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))',
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))',
      }}
      onKeyDown={handleKeyPress}
      tabIndex={0}
    >
      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="w-full max-w-md rounded-lg p-6 text-center">
        <h1
          className="text-2xl font-bold text-white mb-4"
          onClick={handleReloadTap}
          role="button"
          tabIndex={-1}
          aria-label="3回タップで再読み込み"
        >
          歯科医院申し送りシステム
        </h1>
        <p className="text-white mb-4">PINを入力してください</p>
        {staffLoading && (
          <p className="text-sm text-white/70 mb-2">読み込み中...</p>
        )}
        {pinError && (
          <p className="text-sm text-red-200 bg-red-900/40 border border-red-400/50 rounded-md px-3 py-2 mb-2">
            {pinError}
          </p>
        )}
        <div className="flex justify-center mb-4">
          <Lock className="w-5 h-5 text-white" />
        </div>
        <div className="space-y-8">
          {/* PIN Display */}
          <div className="flex justify-center space-x-3">
            {[0, 1, 2, 3].map((index) => (
              <div
                key={index}
                className="w-16 h-20 flex items-center justify-center bg-white text-3xl font-bold rounded-md text-black"
                style={{ border: 'none', outline: 'none' }}
              >
                {pin[index] || ''}
              </div>
            ))}
          </div>

          {/* Number Pad */}
          <div className="grid grid-cols-3 gap-x-2 gap-y-6 max-w-xs mx-auto place-items-center">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((number) => (
              <Button
                key={number}
                className="aspect-square h-20 w-20 rounded-full text-2xl font-semibold flex items-center justify-center bg-white hover:bg-gray-100 text-black"
                onClick={() => handleNumberClick(number.toString())}
                disabled={loading || staffLoading}
              >
                {number}
              </Button>
            ))}
            
            <Button
              className="aspect-square h-20 w-20 rounded-full text-base flex items-center justify-center bg-white hover:bg-gray-100 text-black"
              onClick={handleClear}
              disabled={loading || staffLoading}
            >
              クリア
            </Button>

            <Button
              className="aspect-square h-20 w-20 rounded-full text-2xl font-semibold flex items-center justify-center bg-white hover:bg-gray-100 text-black"
              onClick={() => handleNumberClick('0')}
              disabled={loading || staffLoading}
            >
              0
            </Button>

            <Button
              className="aspect-square h-20 w-20 rounded-full text-base flex items-center justify-center bg-white hover:bg-gray-100 text-black"
              onClick={handleBackspace}
              disabled={loading || staffLoading}
            >
              削除
            </Button>
          </div>

        </div>
      </div>
      </div>
    </div>
  );
}